// src/components/registration/steps/claimsSteps/ClaimsStep2ApprovalGate.jsx

import React, { useState } from "react";
import PropTypes from "prop-types";
import MultiStyleButton from "../../../../systemUtils/MultiStyleButton";

/**
 * ClaimsStep2ApprovalGate
 *
 * Single-step approach to configure:
 *  1) Whether approval is required at all.
 *  2) If so, do we need group-based or role-based approvals?
 *  3) Optionally define a numeric threshold that splits coverage:
 *       - Claims <= threshold: Roles/Groups to approve
 *       - Claims > threshold: Roles/Groups to approve
 */
const ClaimsStep2ApprovalGate = ({
  setCardStep,
  companyStructure,
  claimsSystem,
  onChange,
  finishFlow,
}) => {
  const approvalGate = claimsSystem?.approvalGate || {};

  // 1) Basic toggles
  const isApprovalRequired = approvalGate.isRequired || false;
  const needRoleLevel = approvalGate.needRoleLevel || false; // new toggle
  const useValueCondition = approvalGate.useValueCondition || false; // new toggle for threshold coverage

  // 2) For group-based mode (when !needRoleLevel)
  const selectedApproverGroups = approvalGate.approverGroups || [];

  // 3) For role-based mode (when needRoleLevel)
  const selectedApproverRolesBelow = approvalGate.approverRolesBelow || [];
  const selectedApproverRolesAbove = approvalGate.approverRolesAbove || [];

  // 4) Numeric threshold (only relevant if useValueCondition = true)
  const thresholdValue = approvalGate.thresholdValue || "";

  // ----- CHECKBOX HANDLERS -----

  // Master "Require Approval?" checkbox
  const handleApprovalRequiredCheckbox = (e) => {
    const newIsRequired = e.target.checked;
    if (!newIsRequired) {
      // Turn off approvals entirely
      onChange({
        ...claimsSystem,
        approvalGate: {
          isRequired: false,
          needRoleLevel: false,
          useValueCondition: false,
          approverGroups: [],
          approverRolesBelow: [],
          approverRolesAbove: [],
          thresholdValue: "",
        },
      });
    } else {
      // Turn on approvals
      onChange({
        ...claimsSystem,
        approvalGate: {
          ...approvalGate,
          isRequired: true,
        },
      });
    }
  };

  // "Need Role-Level Approval?" checkbox
  const handleNeedRoleLevel = (e) => {
    const newVal = e.target.checked;
    if (newVal) {
      // Switch to role-based => clear group-based
      onChange({
        ...claimsSystem,
        approvalGate: {
          ...approvalGate,
          needRoleLevel: true,
          approverGroups: [], // we won't use groups now
        },
      });
    } else {
      // Switch to group-based => clear role-based
      onChange({
        ...claimsSystem,
        approvalGate: {
          ...approvalGate,
          needRoleLevel: false,
          approverRolesBelow: [],
          approverRolesAbove: [],
          thresholdValue: "",
          useValueCondition: false,
        },
      });
    }
  };

  // "Use Value Condition?" for claims above or below threshold
  const handleUseValueCondition = (e) => {
    const newVal = e.target.checked;
    if (!newVal) {
      // Turn off value splitting => clear threshold & above/below roles
      onChange({
        ...claimsSystem,
        approvalGate: {
          ...approvalGate,
          useValueCondition: false,
          thresholdValue: "",
          // If you want to keep a single set of roles, you could do it differently.
          // For simplicity, we’ll just clear them.
          approverRolesBelow: [],
          approverRolesAbove: [],
        },
      });
    } else {
      // Turn on => keep any existing threshold or set default
      onChange({
        ...claimsSystem,
        approvalGate: {
          ...approvalGate,
          useValueCondition: true,
          thresholdValue: approvalGate.thresholdValue || "5000", // default, e.g. 5000
        },
      });
    }
  };

  // For group-based approach: handle group check
  const handleCheckboxForGroup = (groupName) => {
    let newArr;
    if (selectedApproverGroups.includes(groupName)) {
      newArr = selectedApproverGroups.filter((g) => g !== groupName);
    } else {
      newArr = [...selectedApproverGroups, groupName];
    }
    onChange({
      ...claimsSystem,
      approvalGate: {
        ...approvalGate,
        approverGroups: newArr,
      },
    });
  };

  // For role-based approach: handle roles for below or above threshold
  const handleRoleCheckBelow = (roleKey) => {
    let newArr;
    if (selectedApproverRolesBelow.includes(roleKey)) {
      newArr = selectedApproverRolesBelow.filter((r) => r !== roleKey);
    } else {
      newArr = [...selectedApproverRolesBelow, roleKey];
    }
    onChange({
      ...claimsSystem,
      approvalGate: {
        ...approvalGate,
        approverRolesBelow: newArr,
      },
    });
  };

  const handleRoleCheckAbove = (roleKey) => {
    let newArr;
    if (selectedApproverRolesAbove.includes(roleKey)) {
      newArr = selectedApproverRolesAbove.filter((r) => r !== roleKey);
    } else {
      newArr = [...selectedApproverRolesAbove, roleKey];
    }
    onChange({
      ...claimsSystem,
      approvalGate: {
        ...approvalGate,
        approverRolesAbove: newArr,
      },
    });
  };

  // Numeric input for threshold
  const handleThresholdValue = (e) => {
    const newVal = e.target.value;
    onChange({
      ...claimsSystem,
      approvalGate: {
        ...approvalGate,
        thresholdValue: newVal,
      },
    });
  };

  // ----- GETTING ROLE DATA -----
  const allRoles = (companyStructure?.userGroups || []).flatMap((g) =>
    g.roles.map((r) => ({
      groupName: g.groupName,
      roleName: r.roleName,
    }))
  );
  const getRoleKey = (groupName, roleName) => `${groupName}||${roleName}`;

  // ----- CONTINUE HANDLER -----
  const handleContinue = () => {
    if (isApprovalRequired) {
      if (!needRoleLevel) {
        // group-based
        if (selectedApproverGroups.length === 0) {
          alert(
            "Please select at least one user group if approval is required."
          );
          return;
        }
      } else {
        // role-based
        if (!useValueCondition) {
          // single set of roles covers all claims
          if (
            selectedApproverRolesBelow.length === 0 &&
            selectedApproverRolesAbove.length === 0
          ) {
            alert(
              "Please select at least one user role if approval is required."
            );
            return;
          }
        } else {
          // There's a threshold
          // claims <= threshold => use approverRolesBelow
          // claims > threshold => use approverRolesAbove
          // must ensure coverage for both
          if (!thresholdValue) {
            alert("Please specify a threshold value.");
            return;
          }
          if (selectedApproverRolesBelow.length === 0) {
            alert(
              `Please select at least one user role for claims <= ${thresholdValue}.`
            );
            return;
          }
          if (selectedApproverRolesAbove.length === 0) {
            alert(
              `Please select at least one user role for claims > ${thresholdValue}.`
            );
            return;
          }
        }
      }
    }
    // If passed all validations
    finishFlow();
  };

  return (
    <div>
      <h2 className="card-title">Claims Approval Gate</h2>
      <p className="card-subtitle">
        By default, no approval is required to process claims. If you want an
        approval process, check the box below and select which user groups or
        roles can approve payment requests.
      </p>

      <div className="role-toggle-row">
        <label>
          <input
            type="checkbox"
            checked={isApprovalRequired}
            onChange={handleApprovalRequiredCheckbox}
          />
          Require Approval for processing claims?
        </label>
      </div>

      {/* Show the config only if isApprovalRequired is true */}
      {isApprovalRequired && (
        <div className="approval-frame">
          <p className="approval-title">
            Which user groups or roles will have approval rights for claims?
          </p>

          {/* 1) Checkbox for "Need Role-Level Approval?" */}
          <div className="role-toggle-row">
            <label>
              <input
                type="checkbox"
                checked={needRoleLevel}
                onChange={handleNeedRoleLevel}
              />
              Need Role-Level Approval?
            </label>
          </div>

          {!needRoleLevel && (
            // Group-based checklists
            <>
              <p className="approval-title">
                Please select the user groups that can approve claims.
              </p>
              {companyStructure?.userGroups?.map((g) => (
                <label key={g.groupName} className="approval-group-label">
                  <input
                    type="checkbox"
                    checked={selectedApproverGroups.includes(g.groupName)}
                    onChange={() => handleCheckboxForGroup(g.groupName)}
                  />
                  {g.groupName}
                </label>
              ))}
            </>
          )}

          {needRoleLevel && (
            <>
              {/* 2) Additional checkbox to define a threshold */}
              <div className="role-toggle-row">
                <label>
                  <input
                    type="checkbox"
                    checked={useValueCondition}
                    onChange={handleUseValueCondition}
                  />
                  Specify a value condition? (e.g. claims > X need different
                  roles)
                </label>
              </div>

              {!useValueCondition ? (
                // Single set of roles covers all claims
                <>
                  <p className="approval-title">
                    Please select roles that can approve ANY claims.
                  </p>
                  {allRoles.map((rObj) => {
                    const rKey = getRoleKey(rObj.groupName, rObj.roleName);
                    const isCheckedBelow =
                      selectedApproverRolesBelow.includes(rKey);
                    // We'll store them in approverRolesBelow for simplicity
                    return (
                      <label key={rKey} className="approval-group-label">
                        <input
                          type="checkbox"
                          checked={isCheckedBelow}
                          onChange={() => handleRoleCheckBelow(rKey)}
                        />
                        {rObj.groupName} - {rObj.roleName}
                      </label>
                    );
                  })}
                </>
              ) : (
                // We have a threshold: <= threshold => rolesBelow, > threshold => rolesAbove
                <>
                  <p className="approval-title">
                    Claims &lt;= a certain threshold:
                  </p>

                  <label className="threshold-label">
                    Threshold Value:
                    <input
                      type="number"
                      value={thresholdValue}
                      onChange={handleThresholdValue}
                      style={{ width: "100px", marginLeft: "8px" }}
                    />
                  </label>
                  <p>For claims &lt;= {thresholdValue}, select roles:</p>
                  {allRoles.map((rObj) => {
                    const rKey = getRoleKey(rObj.groupName, rObj.roleName);
                    const isCheckedBelow =
                      selectedApproverRolesBelow.includes(rKey);
                    return (
                      <label key={rKey} className="approval-group-label">
                        <input
                          type="checkbox"
                          checked={isCheckedBelow}
                          onChange={() => handleRoleCheckBelow(rKey)}
                        />
                        {rObj.groupName} - {rObj.roleName}
                      </label>
                    );
                  })}

                  <hr style={{ margin: "15px 0" }} />

                  <p>For claims &gt; {thresholdValue}, select roles:</p>
                  {allRoles.map((rObj) => {
                    const rKey = getRoleKey(rObj.groupName, rObj.roleName);
                    const isCheckedAbove =
                      selectedApproverRolesAbove.includes(rKey);
                    return (
                      <label key={rKey} className="approval-group-label">
                        <input
                          type="checkbox"
                          checked={isCheckedAbove}
                          onChange={() => handleRoleCheckAbove(rKey)}
                        />
                        {rObj.groupName} - {rObj.roleName}
                      </label>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
      )}

      <div className="continue-button-container">
        <MultiStyleButton
          label="Back"
          buttonType="cancel"
          onClick={() => setCardStep(1)}
        />
        <MultiStyleButton
          label="Finish"
          buttonType="approve"
          onClick={handleContinue}
        />
      </div>
    </div>
  );
};

ClaimsStep2ApprovalGate.propTypes = {
  setCardStep: PropTypes.func.isRequired,
  companyStructure: PropTypes.object.isRequired,
  claimsSystem: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  finishFlow: PropTypes.func.isRequired,
};

export default ClaimsStep2ApprovalGate;
